import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from "lucide-react";
import GameCarousel from '../GameCarousel/GameCarousel';
import GameGrid from '../GameGrid/GameGrid';
import Pagination from '../Pagination/Pagination';
import { preloadImages } from '../LazyImage/LazyImage';

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 12px 0px;
`;

const TopTitle = styled.div`
  color: white;
  font-size: 16px;
  font-weight: bold;
  margin-left: 4px;
`;

const IconBg = styled.div`
  padding: 6px 8px;
  background-color: #03030F;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover {
    background-color: #4a5354;
  }
`;

// ============ Skeleton loader ============ //
const SkeletonRow = styled.div`
  display: flex;
  gap: 11px;
  overflow: hidden;
  padding: 2px 0;
`;

const SkeletonCard = styled.div`
  min-width: 142px;
  height: 195px;
  flex-shrink: 0;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(160deg, #20253a 0%, #161a2b 55%, #0e1120 100%);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.07) 45%,
      rgba(255, 196, 0, 0.06) 50%,
      rgba(255, 255, 255, 0.07) 55%,
      transparent 100%
    );
    animation: gs-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes gs-shimmer {
    100% { transform: translateX(100%); }
  }

  @media (max-width: 768px) {
    min-width: 115px;
    height: 160px;
  }
`;

const SkeletonTitle = styled.div`
  width: 120px;
  height: 18px;
  border-radius: 6px;
  margin: 12px 0 12px 4px;
  position: relative;
  overflow: hidden;
  background: #1b2032;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
    animation: gs-shimmer 1.4s ease-in-out infinite;
  }
`;

const GameSectionSkeleton = ({ title, count = 8 }) => (
  <div style={{ marginTop: '6px' }}>
    <div style={{ margin: '12px 0' }}>
      {title ? <TopTitle>{title}</TopTitle> : <SkeletonTitle />}
    </div>
    <SkeletonRow>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </SkeletonRow>
  </div>
);

const GameSection = ({
  title,
  games = [],
  onGameClick,
  vendor,
  vendorPagination,
  onPagination,
  enabled = true,
  showMoreState,
  onToggleShowMore,
  scrollContainerRef,
  onScrollLeft,
  onScrollRight,
  isMobileView = false,
  loading = false
}) => {
  const [showMore, setShowMore] = useState(showMoreState || false);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [imageErrors, setImageErrors] = useState(new Set());

  // Preload images when games change
  useEffect(() => {
    if (games && games.length > 0) {
      const imageUrls = games.slice(0, 15).map(game => game.game_icon);
      preloadImages(imageUrls, 4); // Load 4 at a time
    }
  }, [games]);

  const handleImageLoad = useCallback((gameId) => {
    setLoadedImages(prev => new Set([...prev, gameId]));
  }, []);

  const handleImageError = useCallback((gameId) => {
    setImageErrors(prev => new Set([...prev, gameId]));
  }, []);

  const handleToggleShowMore = () => {
    const newShowMore = !showMore;
    setShowMore(newShowMore);
    if (onToggleShowMore) {
      onToggleShowMore(newShowMore);
    }
  };

  if (!enabled) return null;

  const hasGames = games && games.length > 0;

  // While games for this section are still loading, show a shimmer skeleton row
  // instead of collapsing the section (keeps layout stable + looks polished).
  if (!hasGames) {
    if (loading) {
      return <GameSectionSkeleton title={title} />;
    }
    return null;
  }

  const displayGames = isMobileView
    ? games
    : (showMore ? games : games.slice(0, 40));

  return (
    <div style={{ marginTop: "6px" }}>
      <HeaderContainer>
        <TopTitle>{title}</TopTitle>
        <HeaderContent>
          <div onClick={handleToggleShowMore}>
            <IconBg>
              <div style={{ fontWeight: "bold" }}>
                {showMore ? "Show Less" : "Show More"}
              </div>
            </IconBg>
          </div>
          {!showMore && (
            <div style={{ display: "flex", gap: '4px' }}>
              <IconBg onClick={onScrollLeft}>
                <ChevronLeft size={16} />
              </IconBg>
              <IconBg onClick={onScrollRight}>
                <ChevronRight size={16} />
              </IconBg>
            </div>
          )}
        </HeaderContent>
      </HeaderContainer>

      {!showMore ? (
        <GameCarousel
          games={displayGames}
          onGameClick={onGameClick}
          scrollContainerRef={scrollContainerRef}
          onScrollLeft={onScrollLeft}
          onScrollRight={onScrollRight}
          isMobileView={isMobileView}
          onImageLoad={handleImageLoad}
          onImageError={handleImageError}
        />
      ) : (
        <>
          <GameGrid
            games={displayGames}
            onGameClick={onGameClick}
            loading={loading}
            onImageLoad={handleImageLoad}
            onImageError={handleImageError}
          />
          {vendor && vendorPagination && onPagination && (
            <Pagination
              vendor={vendor}
              pagination={vendorPagination}
              onPagination={onPagination}
            />
          )}
        </>
      )}
    </div>
  );
};

export default GameSection;
